import { Pipe, PipeTransform } from '@angular/core';

interface NumbersObject {
  [key: number]: string;
  [key: string]: string;
}

@Pipe({ name: 'numberToText' })
export class NumberToTextPipe implements PipeTransform {
  private numbers: NumbersObject = {
    0: '',
    '00': '',
    '000': '',
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four',
    5: 'five',
    6: 'six',
    7: 'seven',
    8: 'eight',
    9: 'nine',
    10: 'ten',
    11: 'eleven',
    12: 'twelve',
    13: 'thirteen',
    14: 'fourteen',
    15: 'fifteen',
    16: 'sixteen',
    17: 'seventeen',
    18: 'eighteen',
    19: 'nineteen',
    20: 'twenty',
    30: 'thirty',
    40: 'forty',
    50: 'fifty',
    60: 'sixty',
    70: 'seventy',
    80: 'eighty',
    90: 'ninety',
  };

  transform(value: number | string, hasDecimal: boolean): string {
    let numberWords: string[] = [];

    if(!value) {
      return '';
    }

    if (typeof value !== 'string') {
      value = value.toString();
    }

    if (value.indexOf(',') > -1) {
      const re = new RegExp(',', 'g');
      value = value.replace(re, '');
    }

    if (!hasDecimal) {
      value = value.split('.')[0];
    }

    let numberString = '';
    const divide = Math.ceil(value.length / 3);
    const splits = [];

    for (let i = 0; i < divide; i++) {
      if (value.length % 3 === 2) {
        splits.push([value[0], value[1]]);
        value = value.substring(2);
      } else if (value.length % 3 === 1) {
        splits.push([value[0]]);
        value = value.substring(1);
      } else {
        splits.push([value[0], value[1], value[2]]);
        value = value.substring(3);
      }
    }

    switch (divide) {
      case 2: {
        numberWords = ['thousand'];
        break;
      }
      case 3: {
        numberWords = ['million', 'thousand'];
        break;
      }
      case 4: {
        numberWords = ['billion', 'million', 'thousand'];
        break;
      }
      case 5: {
        numberWords = ['trillion', 'billion', 'million', 'thousand'];
        break;
      }
    }

    let addAndAfterThisGroup = false;
    let previousScaleUsed = false;

    for (let i = 0; i < splits.length; i++) {
      const split = splits[i];
      const isAllZeros = split.every(digit => digit === '0');

      // Skip if all digits in this split are zero
      if (isAllZeros && (i > 0 || splits.length > 1)) {
        if (numberWords.length > 0) {
          numberWords.shift();
        }
        continue;
      }

      if (numberWords.length > 0) {
        const currentScale = numberWords[0];

        switch (split.length) {
          case 1:
            if (split[0] !== '0') {
              numberString += this.numbers[split[0]] + ' ' + currentScale;
              previousScaleUsed = true;

              // Check if we need to add "and" after this group
              if (currentScale === 'thousand' && i < splits.length - 1) {
                const nextSplit = splits[i + 1];
                if (!nextSplit.every(digit => digit === '0')) {
                  addAndAfterThisGroup = true;
                }
              }
            }
            break;
          case 2:
            if (this.numbers[split[0].toString() + split[1].toString()] !== undefined &&
              split[0].toString() + split[1].toString() !== '00') {
              numberString +=
                this.numbers[split[0].toString() + split[1].toString()] +
                ' ' + currentScale;
              previousScaleUsed = true;

              // Check if we need to add "and" after this group
              if (currentScale === 'thousand' && i < splits.length - 1) {
                const nextSplit = splits[i + 1];
                if (!nextSplit.every(digit => digit === '0')) {
                  addAndAfterThisGroup = true;
                }
              }
            } else if (split[0] !== '0' || split[1] !== '0') {
              if (split[0] !== '0') {
                numberString += this.numbers[split[0] + '0'];
                if (split[1] !== '0') {
                  numberString += ' ' + this.numbers[split[1]];
                }
              } else if (split[1] !== '0') {
                numberString += this.numbers[split[1]];
              }
              numberString += ' ' + currentScale;
              previousScaleUsed = true;

              // Check if we need to add "and" after this group
              if (currentScale === 'thousand' && i < splits.length - 1) {
                const nextSplit = splits[i + 1];
                if (!nextSplit.every(digit => digit === '0')) {
                  addAndAfterThisGroup = true;
                }
              }
            }
            break;
          case 3:
            let hasValue = false;

            if (split[0] !== '0') {
              numberString += this.numbers[split[0]] + ' hundred';
              hasValue = true;
            }

            if (this.numbers[split[1].toString() + split[2].toString()] !== undefined) {
              if (split[1].toString() + split[2].toString() !== '00') {
                if (hasValue) {
                  numberString += ' ';
                }
                numberString += this.numbers[split[1].toString() + split[2].toString()];
                hasValue = true;
              }
            } else {
              if (split[1] !== '0') {
                if (hasValue) {
                  numberString += ' ';
                }
                numberString += this.numbers[split[1] + '0'];
                hasValue = true;

                if (split[2] !== '0') {
                  numberString += ' ' + this.numbers[split[2]];
                }
              } else if (split[2] !== '0') {
                if (hasValue) {
                  numberString += ' ';
                }
                numberString += this.numbers[split[2]];
                hasValue = true;
              }
            }

            if (hasValue) {
              numberString += ' ' + currentScale;
              previousScaleUsed = true;

              // Check if we need to add "and" after this group
              if (currentScale === 'thousand' && i < splits.length - 1) {
                const nextSplit = splits[i + 1];
                if (!nextSplit.every(digit => digit === '0')) {
                  addAndAfterThisGroup = true;
                }
              }
            }
            break;
        }

        numberWords.shift();
        if (previousScaleUsed) {
          numberString += ' ';

          // Add "and" after thousand if the next split has non-zero values
          if (addAndAfterThisGroup) {
            numberString += 'and ';
            addAndAfterThisGroup = false;
          }
        }
      } else {
        // This is the last group (ones, tens, hundreds)        
        switch (split.length) {
          case 1:
            if (split[0] === '0' && numberString === '') {
              numberString += 'zero';
            } else if (split[0] !== '0') {
              numberString += this.numbers[split[0]];
            }
            break;
          case 2:
            if (this.numbers[split[0].toString() + split[1].toString()] !== undefined &&
              split[0].toString() + split[1].toString() !== '00') {
              numberString += this.numbers[split[0].toString() + split[1].toString()];
            } else {
              if (split[0] !== '0') {
                numberString += this.numbers[split[0] + '0'];
                if (split[1] !== '0') {
                  numberString += ' ' + this.numbers[split[1]];
                }
              } else if (split[1] !== '0') {
                numberString += this.numbers[split[1]];
              }
            }
            break;
          case 3:
            let hasValue = false;

            if (split[0] !== '0') {
              numberString += this.numbers[split[0]] + ' hundred';
              hasValue = true;
            }

            if (this.numbers[split[1].toString() + split[2].toString()] !== undefined) {
              if (split[1].toString() + split[2].toString() !== '00') {
                if (hasValue) {
                  numberString += ' ';
                }
                numberString += this.numbers[split[1].toString() + split[2].toString()];
              }
            } else {
              if (split[1] !== '0') {
                if (hasValue) {
                  numberString += ' ';
                }
                numberString += this.numbers[split[1] + '0'];
                if (split[2] !== '0') {
                  numberString += ' ' + this.numbers[split[2]];
                }
              } else if (split[2] !== '0') {
                if (hasValue) {
                  numberString += ' ';
                }
                numberString += this.numbers[split[2]];
              }
            }
            break;
        }
      }
    }

    return numberString.trim();
  }
}
